// src/lib/data/sync/RealtimeSync.js - V1 ENTERPRISE ULTRA-ROBUST
// ═══════════════════════════════════════════════════════════════════════════
// 🔄 REALTIME SYNC ENGINE — Google Docs / Notion / Figma Grade
// ═══════════════════════════════════════════════════════════════════════════
// Propósito: Sincronización en tiempo real entre múltiples dispositivos
//
// 🔥 V1: ON SNAPSHOT — Suscripción a cambios en Firestore (< 1s entre dispositivos)
// 🔥 V1: INDEXEDDB SYNC — Actualiza almacenamiento local automáticamente
// 🔥 V1: UI NOTIFICATIONS — Eventos DOM para actualizar React sin recargar
// 🔥 V1: DEBOUNCE — Protección contra ráfagas de actualizaciones (< 300ms)
// 🔥 V1: BATCH UPDATES — Agrupa múltiples cambios en una sola notificación
// 🔥 V1: RECONEXIÓN AUTOMÁTICA — Reintentos exponenciales si Firestore falla
// 🔥 V1: MULTI-COLECCIÓN — properties, clients, tasks simultáneamente
// 🔥 V1: TOMBSTONE AWARE — No resucita documentos eliminados
// 🔥 V1: ZERO DEPENDENCIAS NUEVAS — Usa CloudStrategy.onSnapshot existente
// 🔥 V1: MEMORIA EFICIENTE — Limpieza automática al desuscribir
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// 📐 CONFIGURACIÓN DE PRODUCCIÓN
// ═══════════════════════════════════════════════════════════════════════════

const CONFIG = {
  // Tiempo mínimo entre notificaciones a la UI (anti-parpadeo)
  DEBOUNCE_MS: 300,
  
  // Máximo de cambios acumulados antes de notificar (batch)
  MAX_BATCH_SIZE: 50,
  
  // Tiempo máximo de espera para acumular cambios (batch)
  BATCH_MAX_WAIT_MS: 500,
  
  // Reintentos de reconexión
  MAX_RECONNECT_ATTEMPTS: 5,
  RECONNECT_BASE_DELAY: 1000,
  RECONNECT_MAX_DELAY: 30_000,
  
  // Colecciones a sincronizar en tiempo real
  COLLECTIONS: ['properties', 'clients', 'tasks'],
  
  // Eventos DOM emitidos
  EVENTS: {
    DATA_CHANGED: 'realtime:dataChanged',
    CONNECTION_CHANGED: 'realtime:connectionChanged',
    SYNC_ERROR: 'realtime:syncError',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// 🛠️ UTILITARIOS DE ALTO RENDIMIENTO
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Debounce para evitar notificaciones excesivas
 */
function createDebouncer(delayMs) {
  let timer = null;
  let pending = [];
  
  return {
    add: (item) => {
      pending.push(item);
    },
    flush: (callback) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const batch = [...pending];
        pending = [];
        if (batch.length > 0) {
          callback(batch);
        }
      }, delayMs);
    },
    cancel: () => {
      if (timer) clearTimeout(timer);
      pending = [];
    },
    get size() {
      return pending.length;
    }
  };
}

/**
 * Verifica si un documento debe ser ignorado (tombstone)
 */
function _isTombstoned(item) {
  if (!item) return false;
  if (item._deleted === true) return true;
  if (item._isDeleted === true) return true;
  if (item.tombstone === true) return true;
  if (item.deleted === true) return true;
  return false;
}

/**
 * Extrae el ID de un documento de forma segura
 */
function _safeId(item) {
  return String(item?.id || item?.docId || item?._id || '');
}

// ═══════════════════════════════════════════════════════════════════════════
// 🚀 REALTIME SYNC ENGINE — CLASE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

class RealtimeSync {
  /**
   * @param {Object} deps - Dependencias inyectadas
   * @param {Object} deps.cloud - CloudStrategy instance (requerido)
   * @param {Object} deps.local - LocalStrategy instance (requerido)
   * @param {Object} deps.syncManager - SyncManager instance (opcional)
   * @param {string} deps.userId - ID del usuario
   * @param {string} deps.companyId - ID de la compañía
   */
  constructor(deps = {}) {
    this.cloud = deps.cloud || null;
    this.local = deps.local || null;
    this.syncManager = deps.syncManager || null;
    this.userId = deps.userId || 'unknown';
    this.companyId = deps.companyId || 'unknown';
    
    // Estado de suscripciones
    this._subscriptions = new Map();
    this._unsubscribers = new Map();
    this._debouncers = new Map();
    this._observers = [];
    
    // Estado de conexión
    this._isConnected = false;
    this._reconnectAttempts = 0;
    this._reconnectTimers = new Map();
    
    // Métricas
    this._metrics = {
      totalChanges: 0,
      lastChange: null,
      byCollection: {},
      errors: 0,
      reconnections: 0,
    };
    
    // Flags
    this._started = false;
    this._destroyed = false;
    
    console.log(
      `🔄 RealtimeSync V1 — User: ${this.userId} | Company: ${this.companyId} | ` +
      `Cloud: ${!!this.cloud} | Local: ${!!this.local} | ` +
      `Colecciones: [${CONFIG.COLLECTIONS.join(', ')}]`
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🚀 INICIAR / DETENER
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Inicia la sincronización en tiempo real para todas las colecciones
   * 
   * @param {Array<string>} collections - Colecciones a sincronizar (default: todas)
   * @returns {Object} Resultado con conteo de suscripciones
   */
  start(collections = null) {
    if (this._destroyed) {
      console.warn('⚠️ [RealtimeSync] No se puede iniciar: instancia destruida');
      return { success: false, error: 'destroyed' };
    }
    
    if (!this.cloud || !this.cloud.isOnline) {
      console.warn('⚠️ [RealtimeSync] No se puede iniciar: CloudStrategy no disponible');
      this._scheduleReconnect();
      return { success: false, error: 'cloud_unavailable' };
    }
    
    const cols = collections || CONFIG.COLLECTIONS;
    
    console.log(`🔄 [RealtimeSync] Iniciando tiempo real para: [${cols.join(', ')}]`);
    
    let subscribed = 0;
    
    for (const collectionName of cols) {
      try {
        this._subscribeToCollection(collectionName);
        subscribed++;
      } catch (error) {
        console.error(`❌ [RealtimeSync] Error suscribiendo a ${collectionName}:`, error.message);
        this._metrics.errors++;
      }
    }
    
    this._started = true;
    this._isConnected = true;
    this._notifyConnectionChange(true);
    
    console.log(`✅ [RealtimeSync] ${subscribed}/${cols.length} colecciones en tiempo real`);
    
    return {
      success: subscribed > 0,
      subscribed,
      total: cols.length,
      collections: cols,
    };
  }

  /**
   * Detiene todas las suscripciones
   */
  stop() {
    console.log('🛑 [RealtimeSync] Deteniendo todas las suscripciones...');
    
    let unsubscribed = 0;
    
    this._unsubscribers.forEach((unsubscribe, key) => {
      try {
        unsubscribe();
        unsubscribed++;
      } catch (e) {
        console.warn(`⚠️ [RealtimeSync] Error al desuscribir ${key}:`, e.message);
      }
    });
    
    this._unsubscribers.clear();
    this._subscriptions.clear();
    
    // Cancelar debouncers
    this._debouncers.forEach(debouncer => debouncer.cancel());
    this._debouncers.clear();
    
    // Cancelar reconexiones pendientes
    this._reconnectTimers.forEach(timer => clearTimeout(timer));
    this._reconnectTimers.clear();
    
    this._started = false;
    this._isConnected = false;
    this._notifyConnectionChange(false);
    
    console.log(`✅ [RealtimeSync] ${unsubscribed} suscripciones detenidas`);
    
    return { success: true, unsubscribed };
  }

  /**
   * Reinicia las suscripciones (útil después de reconexión)
   */
  restart() {
    console.log('🔄 [RealtimeSync] Reiniciando...');
    this.stop();
    return this.start();
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 📡 SUSCRIPCIÓN POR COLECCIÓN
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Suscribe a cambios en una colección específica
   */
  _subscribeToCollection(collectionName) {
    const listenerKey = `realtime_${collectionName}_${this.companyId}`;
    
    // Evitar suscripciones duplicadas
    if (this._unsubscribers.has(listenerKey)) {
      console.log(`⏭️ [RealtimeSync] Ya suscrito a ${collectionName}`);
      return;
    }
    
    // Crear debouncer para esta colección
    if (!this._debouncers.has(collectionName)) {
      this._debouncers.set(collectionName, createDebouncer(CONFIG.DEBOUNCE_MS));
    }
    
    // Suscribir usando CloudStrategy
    const unsubscribe = this.cloud.subscribeToCollection(
      collectionName,
      (data, changes) => {
        this._handleCollectionChange(collectionName, data, changes);
      }
    );
    
    this._unsubscribers.set(listenerKey, unsubscribe);
    this._subscriptions.set(listenerKey, {
      collection: collectionName,
      startedAt: new Date().toISOString(),
      status: 'active',
    });
    
    console.log(`📡 [RealtimeSync] Suscrito a ${collectionName}`);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔥 MANEJO DE CAMBIOS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Maneja cambios recibidos de Firestore
   */
  async _handleCollectionChange(collectionName, cloudData, changes) {
    if (this._destroyed) return;
    
    // Filtrar cambios relevantes
    const relevantChanges = (changes || []).filter(change => {
      const data = change.data || {};
      
      // Ignorar documentos eliminados (tombstone)
      if (_isTombstoned(data)) {
        return false;
      }
      
      // Solo cambios de nuestro companyId
      if (data.companyId && data.companyId !== this.companyId) {
        return false;
      }
      
      return true;
    });
    
    if (relevantChanges.length === 0) return;
    
    // Actualizar métricas
    this._metrics.totalChanges += relevantChanges.length;
    this._metrics.lastChange = new Date().toISOString();
    
    if (!this._metrics.byCollection[collectionName]) {
      this._metrics.byCollection[collectionName] = 0;
    }
    this._metrics.byCollection[collectionName] += relevantChanges.length;
    
    console.log(
      `📡 [RealtimeSync] ${collectionName}: ${relevantChanges.length} cambios | ` +
      `Tipos: ${relevantChanges.map(c => c.type).join(', ')}`
    );
    
    // Sincronizar con IndexedDB
    await this._syncToLocal(collectionName, cloudData, relevantChanges);
    
    // Notificar a la UI (con debounce)
    this._notifyUI(collectionName, relevantChanges, cloudData);
  }

  /**
   * Sincroniza cambios con IndexedDB
   */
  async _syncToLocal(collectionName, cloudData, changes) {
    if (!this.local) return;
    
    try {
      for (const change of changes) {
        const data = change.data || {};
        const id = _safeId(data) || change.id;
        
        if (!id) continue;
        
        switch (change.type) {
          case 'added':
          case 'modified': {
            // Verificar si no es un tombstone
            if (_isTombstoned(data)) continue;
            
            // Guardar en local (upsert)
            if (typeof this.local.save === 'function') {
              await this.local.save(collectionName, { ...data, id, _realtimeSync: true }, id);
            } else if (typeof this.local.update === 'function') {
              await this.local.update(collectionName, id, { ...data, _realtimeSync: true });
            }
            break;
          }
          
          case 'removed': {
            // Marcar como eliminado
            if (typeof this.local.createTombstone === 'function') {
              await this.local.createTombstone(collectionName, id, {
                userId: this.userId,
                reason: 'realtime_remote_delete',
              });
            }
            if (typeof this.local.delete === 'function') {
              await this.local.delete(collectionName, id, { silent: true });
            }
            break;
          }
        }
      }
      
      // Invalidar caché de la estrategia local
      if (typeof this.local.invalidateCache === 'function') {
        this.local.invalidateCache(collectionName);
      }
      
    } catch (error) {
      console.warn(`⚠️ [RealtimeSync] Error sincronizando ${collectionName} a local:`, error.message);
    }
  }

  /**
   * Notifica a la UI sobre cambios (con debounce)
   */
  _notifyUI(collectionName, changes, fullData) {
    const debouncer = this._debouncers.get(collectionName);
    if (!debouncer) return;
    
    // Agregar cambios al batch
    for (const change of changes) {
      debouncer.add({
        type: change.type,
        id: change.id || _safeId(change.data),
        collection: collectionName,
        timestamp: new Date().toISOString(),
      });
    }
    
    // Si el batch es grande, notificar inmediatamente
    if (debouncer.size >= CONFIG.MAX_BATCH_SIZE) {
      this._emitUIEvent(collectionName, fullData);
      return;
    }
    
    // Flush con debounce
    debouncer.flush(() => {
      this._emitUIEvent(collectionName, fullData);
    });
  }

  /**
   * Emite evento DOM para que React actualice la UI
   */
  _emitUIEvent(collectionName, fullData) {
    if (typeof window === 'undefined') return;
    
    try {
      window.dispatchEvent(new CustomEvent(CONFIG.EVENTS.DATA_CHANGED, {
        detail: {
          collection: collectionName,
          count: fullData?.length || 0,
          timestamp: new Date().toISOString(),
          source: 'realtime',
        }
      }));
    } catch (e) {
      // Silencioso - el evento es opcional
    }
    
    // También notificar a observadores directos
    this._notifyObservers({
      type: 'dataChanged',
      collection: collectionName,
      count: fullData?.length || 0,
      timestamp: new Date().toISOString(),
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔄 RECONEXIÓN AUTOMÁTICA
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Programa reconexión cuando Firestore se desconecta
   */
  _scheduleReconnect() {
    if (this._reconnectAttempts >= CONFIG.MAX_RECONNECT_ATTEMPTS) {
      console.error(`❌ [RealtimeSync] Máximo de reintentos alcanzado (${CONFIG.MAX_RECONNECT_ATTEMPTS})`);
      this._notifyConnectionChange(false);
      return;
    }
    
    const delay = Math.min(
      CONFIG.RECONNECT_BASE_DELAY * Math.pow(2, this._reconnectAttempts),
      CONFIG.RECONNECT_MAX_DELAY
    );
    
    this._reconnectAttempts++;
    this._metrics.reconnections++;
    
    console.log(`🔄 [RealtimeSync] Reintentando en ${delay}ms (intento ${this._reconnectAttempts}/${CONFIG.MAX_RECONNECT_ATTEMPTS})...`);
    
    const timer = setTimeout(() => {
      this._reconnectTimers.delete('main');
      
      if (this.cloud?.isOnline) {
        console.log('🔄 [RealtimeSync] Reconectando...');
        this._reconnectAttempts = 0;
        this.start();
      } else {
        this._scheduleReconnect();
      }
    }, delay);
    
    this._reconnectTimers.set('main', timer);
  }

  /**
   * Notifica cambio de estado de conexión
   */
  _notifyConnectionChange(connected) {
    if (typeof window === 'undefined') return;
    
    try {
      window.dispatchEvent(new CustomEvent(CONFIG.EVENTS.CONNECTION_CHANGED, {
        detail: {
          connected,
          timestamp: new Date().toISOString(),
        }
      }));
    } catch (e) {
      // Silencioso
    }
    
    this._notifyObservers({
      type: 'connectionChange',
      connected,
      timestamp: new Date().toISOString(),
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 📡 OBSERVADORES (Patrón Observer)
  // ═══════════════════════════════════════════════════════════════════════

  subscribe(callback) {
    if (typeof callback === 'function') {
      this._observers.push(callback);
      return () => {
        this._observers = this._observers.filter(o => o !== callback);
      };
    }
    return () => {};
  }

  _notifyObservers(event) {
    this._observers.forEach(callback => {
      try {
        callback(event);
      } catch (e) {
        // Silencioso
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 📊 ESTADO Y MÉTRICAS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Obtiene el estado actual del motor de tiempo real
   */
  getStatus() {
    const subscriptions = [];
    this._subscriptions.forEach((sub, key) => {
      subscriptions.push({ key, ...sub });
    });
    
    return {
      version: 'V1',
      started: this._started,
      connected: this._isConnected,
      subscriptions,
      collections: CONFIG.COLLECTIONS,
      metrics: { ...this._metrics },
      cloudAvailable: !!this.cloud?.isOnline,
      localAvailable: !!this.local,
    };
  }

  /**
   * Verifica si una colección específica está siendo sincronizada
   */
  isCollectionSynced(collectionName) {
    const listenerKey = `realtime_${collectionName}_${this.companyId}`;
    return this._unsubscribers.has(listenerKey);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ♻️ DESTRUCCIÓN
  // ═══════════════════════════════════════════════════════════════════════

  destroy() {
    console.log('♻️ [RealtimeSync] Destruyendo...');
    
    this.stop();
    
    this._observers = [];
    this._subscriptions.clear();
    this._debouncers.clear();
    this._reconnectTimers.clear();
    
    this._destroyed = true;
    
    console.log('✅ [RealtimeSync] Destruido');
  }
}

export default RealtimeSync;
export { CONFIG as RealtimeSyncConfig };